import { LayoutStateDT } from "@context/layout";
import { useRouter } from "next/router";
import { useEffect, useState, useRef, useCallback } from "react";

export type DebugLogDT =
  | {
      user: {
        first_name: string;
        last_name: string;
        email?: string | null;
        mobile?: string | null;
      } | null;
      user_id: number | null;
      isLoggedIn: boolean;
      isAdmin: boolean;
      timestamp: number;
      timeDiff: number;
      path: string;
    }
  | { message: string; timestamp: number };

const DEBUG_LOG_KEY = "debug_log";
const START_LOGGING_KEY = "start_debug_logging";

const useDebugMode = (layoutSate: LayoutStateDT) => {
  const { asPath } = useRouter();
  const { userData } = layoutSate;

  const [savedLogs, setSavedLogs] = useState<DebugLogDT[]>([]);
  const [startLogging, setStartLogging] = useState(false);

  const isInitialized = useRef(false);

  const addLogHandler = useCallback(() => {
    const lastTime = savedLogs[0]?.timestamp || 0;
    const newTime = Date.now();
    const newRecord: DebugLogDT = {
      user: userData?.user
        ? {
            email: userData.user?.email,
            first_name: userData.user?.first_name,
            last_name: userData.user?.last_name,
            mobile: userData.user?.mobile,
          }
        : null,
      user_id: userData?.user_id || null,
      isAdmin: !!userData?.user?.isAdmin,
      isLoggedIn: !!userData?.is_login,
      timestamp: newTime,
      timeDiff: newTime - lastTime,
      path: asPath,
    };
    setSavedLogs((prev) => {
      const newVal = [...prev, newRecord];
      sessionStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(newVal));
      return newVal;
    });
  }, [userData?.user, userData?.is_login, asPath]);

  const restoreLogsHandler = () => {
    if (!isInitialized?.current) {
      isInitialized.current = true;
      const oldVals = sessionStorage.getItem(DEBUG_LOG_KEY);
      if (oldVals) {
        const restoredVals = JSON.parse(oldVals);
        if (Array.isArray(restoredVals)) {
          const lastTime = restoredVals[0]?.timestamp || 0;
          const newTime = Date.now();
          const newRecord: DebugLogDT = {
            user: userData?.user
              ? {
                  email: userData.user?.email,
                  first_name: userData.user?.first_name,
                  last_name: userData.user?.last_name,
                  mobile: userData.user?.mobile,
                }
              : null,
            user_id: userData?.user_id || null,
            isAdmin: !!userData?.user?.isAdmin,
            isLoggedIn: !!userData?.is_login,
            timestamp: newTime,
            timeDiff: newTime - lastTime,
            path: asPath,
          };

          setSavedLogs([
            ...restoredVals,
            {
              message: "Hard refresh occured, logs restored",
              timestamp: Date.now(),
            },
            newRecord,
          ]);
        }
      }
    }
  };

  const exportLogsHandler = useCallback(() => {
    const blob = new Blob([JSON.stringify(savedLogs, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `faradars-debug-logs-${Date.now()}.json`;
    a.click();
  }, [savedLogs]);

  useEffect(() => {
    const isEnabledBefore = Boolean(localStorage.getItem(START_LOGGING_KEY));
    if (isEnabledBefore) setStartLogging(true);
  }, []);

  useEffect(() => {
    restoreLogsHandler();
    if (startLogging) addLogHandler();
    else if (asPath && asPath.includes("debug_mode=1")) {
      setStartLogging(true);
      localStorage.setItem(START_LOGGING_KEY, "1");
      addLogHandler();
    }
  }, [addLogHandler]);

  useEffect(() => {
    if (startLogging) {
      const shortCutHandler = (e: any) => {
        // Ctrl + Shift + E → export logs
        if (e.ctrlKey && e.shiftKey && e.key === "E") {
          e.preventDefault();
          exportLogsHandler();
        }

        if (e.ctrlKey && e.shiftKey && e.key === "S") {
          e.preventDefault();
          alert(`Logger is on! ${savedLogs.length} records`);
        }

        if (e.ctrlKey && e.shiftKey && e.key === "C") {
          e.preventDefault();
          setSavedLogs([]);
          localStorage.setItem(DEBUG_LOG_KEY, "[]");
          alert("Logger cleared!");
        }

        if (e.ctrlKey && e.shiftKey && e.key === "X") {
          e.preventDefault();
          setSavedLogs([]);
          localStorage.removeItem(DEBUG_LOG_KEY);
          setStartLogging(false);
          window.removeEventListener("keydown", shortCutHandler);
          alert("Logger turned off!");
        }
      };

      window.addEventListener("keydown", shortCutHandler);
      return () => window.removeEventListener("keydown", shortCutHandler);
    }
  }, [startLogging, exportLogsHandler]);

  return savedLogs;
};

export default useDebugMode;
